// All copy for the portfolio in one place.
// Edit here to update content site-wide.

export const profile = {
  name: "Ahmad Ilyas",
  initials: "AI",
  role: "Software Engineer",
  positioning: "Turning complex systems into clean, maintainable solutions.",
  location: "East Java, Indonesia",
  email: "ahmadilyasdahlan@gmail.com",
  resumeUrl: "/resume-ahmad-ilyas.pdf",
  philosophy:
    "A good system doesn’t just work. It stays stable, structured, and built to evolve.",
  philosophyTranslation:
    "Sistem yang baik bukan sekadar berjalan saat ini — melainkan tetap stabil, terstruktur, dan siap berkembang ke depannya.",
  socials: [
    { label: "GitHub", href: "https://github.com/juniyasyos" },
    { label: "LinkedIn", href: "https://linkedin.com/in/ahmad-ilyas-dahlan" },
    { label: "X / Twitter", href: "https://x.com/juniyasyos" },
    { label: "Email", href: "mailto:ahmadilyasdahlan@gmail.com" },
  ],
};

// Active / in-progress work shown in Hero
export const currentlyBuilding = [
  {
    label: "SIIMUT v2",
    description: "Rewrite arsitektur modul mutu dengan event-sourcing",
    status: "in progress",
  },
  {
    label: "Nexaid Auth",
    description: "SSO terpusat berbasis JWT untuk multi-sistem klinis",
    status: "in progress",
  },
];

export const about = {
  intro:
    "Saya membangun sistem dengan fokus ke hal yang paling penting dulu — mana yang benar-benar menopang semuanya.",
  paragraphs: [
    "Saya banyak bekerja di antara web, cloud, dan beberapa aplikasi native. Buat saya, yang penting itu bukan sekadar jalan, tapi jelas strukturnya: batas modul rapi, alur data bisa dipahami, dan deploy yang nggak bikin panik.",
    "Saya lebih suka ngembangin sistem secara bertahap dan terukur. Mulai dari kebutuhan yang jelas, baru ke implementasi. Monitoring dulu, baru optimasi. Feedback cepat lebih penting daripada pakai teknologi yang kelihatan canggih. Sistem yang bagus seharusnya memiliki struktur sederhana.",
  ],
  highlights: [
    { k: "Years building", v: "2+" },
    { k: "Production systems", v: "7" },
    { k: "Domains", v: "Web · Cloud · Native" },
  ],
};

// Local image assets
const BASE = "/images";

const SIIMUT = {
  dashboardAdmin: `${BASE}/siimut/dashboard/dashboard-admin.png`,
  dashboardPic: `${BASE}/siimut/dashboard/dashboard-pic.png`,
  dashboardTimMutu: `${BASE}/siimut/dashboard/dashboard-tim-mutu.png`,
  form: `${BASE}/siimut/daily-reports/form.png`,
  laporanHarian: `${BASE}/siimut/daily-reports/lapora-harian.png`,
  laporanHarianCreate: `${BASE}/siimut/daily-reports/laporan-harian-create.png`,
};

const IKP = Array.from({ length: 10 }, (_, i) => `${BASE}/ikp/ikp${i + 1}.png`);

const NEXAID = Array.from({ length: 4 }, (_, i) => `${BASE}/auth-server/auth${i + 1}.png`);

export const heroTexture = "https://static.prod-images.emergentagent.com/jobs/7a552042-2a1f-4973-b09c-c309778428e4/images/cae40c78afea200787e059a63f6ee46ceee44fc4468e582387247a2507f9c2e1.png";

export const projects = [
  {
    slug: "siimut-platform",
    title: "Sistem Informasi Manajemen Indikator Mutu Terpadu (SIIMUT)",
    tagline: "Platform monitoring dan pelaporan indikator mutu rumah sakit.",
    description:
      "Sistem terintegrasi untuk membantu rumah sakit mengelola pelaporan harian, pemantauan indikator mutu, evaluasi capaian, serta dokumentasi tindak lanjut secara lebih terstruktur. SIIMUT dirancang agar proses pelaporan mutu tetap jelas, konsisten, dan mudah dipantau tanpa memperumit alur kerja unit di lapangan.",
    year: "2026",
    role: "Fullstack Engineer & Team Lead",
    featured: true,
    stack: ["Laravel", "Filament", "Livewire", "Docker"],
    features: [
      "Pelaporan harian indikator mutu berdasarkan unit dan periode",
      "Dashboard monitoring untuk PIC, tim mutu, dan manajemen",
      "Manajemen akses berbasis peran dan struktur organisasi",
      "Rekap, evaluasi, dan dokumentasi capaian indikator mutu",
    ],
    images: [
      SIIMUT.dashboardAdmin,
      SIIMUT.dashboardPic,
      SIIMUT.dashboardTimMutu,
      SIIMUT.form,
      SIIMUT.laporanHarian,
      SIIMUT.laporanHarianCreate,
    ],
  },
  {
    slug: "sp-ikp",
    title: "Sistem Pelaporan Insiden Keselamatan Pasien (SP-IKP)",
    tagline: "Sistem pelaporan insiden yang sederhana, jelas, dan bisa ditindaklanjuti.",
    description:
      "Fokus pada pelaporan insiden keselamatan pasien dengan pendekatan yang lebih ringan dan mudah digunakan. Dibangun agar tenaga kesehatan dapat melaporkan kejadian tanpa hambatan teknis.",
    year: "2026",
    role: "Fullstack Developer & Team Lead",
    featured: true,
    stack: ["Laravel", "Livewire", "Alpine.js", "Docker"],
    features: [
      "Form pelaporan yang sederhana dan cepat digunakan",
      "Klasifikasi insiden otomatis",
      "Tracking status laporan secara transparan",
    ],
    images: IKP,
  },
  {
    slug: "nexaid",
    title: "Nexaid — Identity & Access Management Platform",
    tagline: "Platform autentikasi terpusat dan manajemen akses untuk multi-aplikasi internal.",
    description:
      "Sistem Identity and Access Management (IAM) yang dirancang untuk mengelola autentikasi, profil akses, role, permission, serta distribusi akses pengguna ke berbagai aplikasi internal. Nexaid membantu menyederhanakan proses login, pengaturan hak akses, dan integrasi antar sistem tanpa perlu membuat autentikasi terpisah di setiap aplikasi.",
    year: "2026",
    role: "Fullstack Developer & Team Lead",
    featured: true,
    stack: ["Laravel", "Filament", "JWT", "MySQL", "Docker"],
    features: [
      "Single Sign-On berbasis JWT untuk akses multi-aplikasi",
      "Manajemen pengguna, jabatan, departemen, role, dan permission",
      "Access Profile untuk mengatur paket akses lintas aplikasi",
      "Dashboard admin untuk mengelola status dan hak akses pengguna",
    ],
    images: NEXAID,
  },
  {
    slug: "bumdes-os",
    title: "Desa OS — Village Enterprise Suite",
    tagline: "Sistem operasional untuk BUMDes dan usaha desa.",
    description:
      "Dikembangkan bersama tim penelitian untuk membantu BUMDes mengelola inventaris, keuangan, dan operasional tanpa ketergantungan pada infrastruktur kompleks.",
    year: "2025",
    role: "Research Engineer",
    featured: false,
    stack: ["Next.js", "FastAPI", "PostgreSQL"],
    features: [
      "Manajemen inventaris dan keuangan",
      "UI sederhana untuk pengguna non-teknis",
      "Audit data untuk transparansi operasional",
    ],
    images: [
      "https://static.prod-images.emergentagent.com/jobs/7a552042-2a1f-4973-b09c-c309778428e4/images/bae14f092b03ebd2183441ae6be38289afbf007ac035fe57cef3a5c7bcbe731d.png",
    ],
  },
];

export const experience = [
  {
    year: "Dec 2025 — May 2026",
    role: "Software Engineer (Contract)",
    org: "Healthcare System Project",
    summary:
      "Mengembangkan ekosistem sistem terintegrasi untuk operasional rumah sakit, mencakup sistem pelaporan harian, SP-IKP (pelaporan insiden keselamatan pasien), serta layanan autentikasi terpusat (SSO/IAM). Fokus pada integrasi antar aplikasi, manajemen akses berbasis struktur organisasi, dan kestabilan sistem produksi.",
    tags: ["Fullstack", "System Design", "Authentication", "Healthcare"],
  },
  {
    year: "Mar 2025 — Dec 2025",
    role: "Software Engineer Intern (SIIMUT)",
    org: "Healthcare Project",
    summary:
      "Terlibat dalam pengembangan awal sistem SIIMUT, termasuk perancangan fitur pelaporan, dashboard monitoring, dan struktur data untuk kebutuhan operasional. Bekerja langsung dengan kebutuhan sistem nyata di lingkungan rumah sakit.",
    tags: ["Internship", "Web App", "System Development"],
  },
  {
    year: "Aug 2025 — Dec 2025",
    role: "Research Assistant",
    org: "University Research Team",
    summary:
      "Bergabung dalam tim penelitian untuk mengembangkan platform digital berbasis kebutuhan masyarakat. Mengembangkan E-CoDigimark, sistem rekomendasi tempat wisata dan UMKM di Puger Jember, serta Tamasuma, platform pengembangan skill untuk tutor pendidikan luar sekolah.",
    tags: ["Research", "Web Platform", "Recommendation System"],
  },
  {
    year: "Aug 2025 — Dec 2025",
    role: "Teaching Assistant",
    org: "Deep Learning & Data Visualization",
    summary:
      "Membantu proses pembelajaran pada mata kuliah Deep Learning dan Visual Data. Terlibat dalam pendampingan mahasiswa, evaluasi tugas, serta memastikan pemahaman konsep dasar berjalan dengan baik.",
    tags: ["Teaching", "Deep Learning", "Data Visualization"],
  },
  {
    year: "2024 — 2025",
    role: "Freelance Web Developer",
    org: "Independent",
    summary:
      "Mengerjakan berbagai proyek website menggunakan WordPress, Laravel, dan React. Fokus pada kebutuhan klien, mulai dari website company profile hingga sistem yang digunakan langsung oleh pengguna.",
    tags: ["WordPress", "Laravel", "React", "Client Work"],
  },
];

export const skills = [
  {
    group: "Frontend",
    items: [
      "React",
      "Next.js",
      "TypeScript",
      "Tailwind CSS"
    ],
  },
  {
    group: "Backend",
    items: [
      "Node.js",
      "Python / FastAPI",
      "Redis",
      "PHP",
      "Laravel",
    ],
  },
  {
    group: "Cloud",
    items: [
      "AWS",
      "Docker",
      "Ansible",
      "Terraform",
      "Observability",
    ],
  },
  {
    group: "Native Apps",
    items: [
      "React Native",
      "Electron",
      "Progressive Web Apps",
      "Offline-first patterns",
    ],
  },
];

export const testimonials = [
  {
    name: "Agung Sunaryo S.Kom",
    role: "Kepala IT Support",
    org: "Rumah Sakit Citra Husada Jember",
    project: "SIIMUT & SP-IKP",
    quote:
      "Ilyas memahami kebutuhan teknis kami dengan cepat. Sistem yang dibangun tidak hanya berjalan, tapi juga mudah dipahami oleh tim IT internal kami. Alur data yang rapi dan dokumentasi yang jelas membuat proses handover berjalan lancar.",
    avatar:
      "https://ui-avatars.com/api/?name=Agung+Sunaryo&background=111827&color=ffffff&rounded=true",
  },
  {
    name: "Dr. Yogi Waskito S.Kep, Ners",
    role: "Kepala Mutu",
    org: "Rumah Sakit Citra Husada Jember",
    project: "SIIMUT — Monitoring Mutu",
    quote:
      "Dashboard monitoring mutu yang dibangun benar-benar mengubah cara kami bekerja. Laporan yang sebelumnya manual dan memakan waktu kini bisa diakses real-time. Ilyas sangat responsif terhadap perubahan kebutuhan di lapangan.",
    avatar:
      "https://ui-avatars.com/api/?name=Dr+Yogi+Waskito&background=111827&color=ffffff&rounded=true",
  },
];
